import { ROTULO_PAPEL, schemaAlterarSenha, type EntradaAlterarSenha } from '@baiocchi/shared'
import {
  Alerta,
  Avatar,
  Botao,
  CabecalhoCartao,
  Campo,
  Cartao,
  Confirmacao,
  CorpoCartao,
  Entrada,
  Etiqueta,
  ListaDefinicao,
  RodapeCartao,
  useAvisos,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { autenticacaoApi } from '../api/autenticacao'
import { chaves } from '../api/chaves'
import { useSessao } from '../autenticacao/contexto-sessao'
import { ForcaSenha, senhaAceitavel } from '../componentes/forca-senha'
import { IconeCadeado } from '../componentes/icones'
import { useFormulario } from '../ganchos/usar-formulario'
import { CabecalhoPagina } from '../layout/layout-app'
import { formatarDataHora, formatarTempoRelativo } from '../utilitarios/formatar'

/**
 * TELA-24 — Meu perfil. **[NOVA]**: permite ao usuário gerenciar os próprios dados
 * sem depender do administrador.
 *
 * A troca da própria senha exige a **senha atual** e encerra **todas** as sessões
 * (RNF-17), inclusive a que fez a chamada — por isso a API devolve um par de tokens
 * novo e a tela o aplica em vez de mandar o usuário logar de novo.
 *
 * Quando `deveTrocarSenha` é `true`, a rota protegida traz o usuário para cá e o
 * bloco de senha abre automaticamente: a API responde `403
 * TROCA_SENHA_OBRIGATORIA` em tudo o mais, e mandá-lo para outra tela produziria uma
 * sequência de erros sem explicação.
 */
export function PaginaPerfil() {
  const { usuario, escopo, permissoes, aplicarSessao, sair } = useSessao()
  const [parametros] = useSearchParams()
  const avisos = useAvisos()
  const navegar = useNavigate()

  const trocaObrigatoria = usuario?.deveTrocarSenha ?? false
  const [abrirSenha, setAbrirSenha] = useState(
    trocaObrigatoria || parametros.get('trocar-senha') === '1',
  )
  const [confirmarSairDeTodos, setConfirmarSairDeTodos] = useState(false)

  const sessoes = useQuery({
    queryKey: [...chaves.sessao, 'ativas'],
    queryFn: () => autenticacaoApi.sessoes(),
    retry: false,
  })

  const formulario = useFormulario<EntradaAlterarSenha>({
    senhaAtual: '',
    senhaNova: '',
    confirmacaoSenhaNova: '',
  })

  async function trocarSenha(evento: React.FormEvent) {
    evento.preventDefault()
    const concluiu = await formulario.enviar<EntradaAlterarSenha>(
      schemaAlterarSenha,
      (valores) => valores,
      async (corpo) => {
        const resposta = await autenticacaoApi.alterarSenha(corpo)
        aplicarSessao({ usuario: resposta.usuario, permissoes: resposta.permissoes })
        avisos.sucesso(
          'Senha alterada',
          'As outras sessões foram encerradas. Esta continua ativa.',
        )
      },
    )
    if (concluiu) {
      formulario.reiniciar()
      setAbrirSenha(false)
      void sessoes.refetch()
    }
  }

  if (!usuario) return null

  const podeEnviar =
    formulario.valores.senhaAtual.length > 0 &&
    senhaAceitavel(formulario.valores.senhaNova, {
      email: usuario.email,
      nome: usuario.nome,
    }) &&
    formulario.valores.senhaNova === formulario.valores.confirmacaoSenhaNova

  return (
    <>
      <CabecalhoPagina
        titulo="Meu perfil"
        descricao="Seus dados de acesso e as sessões abertas na sua conta."
      />

      {trocaObrigatoria && (
        <Alerta tom="atencao" titulo="Defina uma senha própria para continuar" className="mb-5">
          Sua senha foi definida por um administrador. Até você trocá-la, o restante do sistema fica
          bloqueado — é a garantia de que ninguém além de você conhece a senha da sua conta.
        </Alerta>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao semPadding>
          <CabecalhoCartao titulo="Identificação" />
          <CorpoCartao className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Avatar nome={usuario.nome} fotoUrl={usuario.fotoUrl} tamanho="xl" />
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-conteudo">{usuario.nome}</p>
                <p className="truncate text-sm text-conteudo-suave">{usuario.email}</p>
                <Etiqueta tom="marca" className="mt-1.5">
                  {ROTULO_PAPEL[usuario.papel]}
                </Etiqueta>
              </div>
            </div>

            <div className="border-t border-borda pt-4">
              <ListaDefinicao
                colunas={2}
                itens={[
                  {
                    rotulo: 'Unidade principal',
                    valor: usuario.unidadePrincipal?.titulo ?? '—',
                  },
                  {
                    rotulo: 'Equipes',
                    valor:
                      usuario.equipes.length === 0
                        ? 'Nenhuma'
                        : usuario.equipes.map((equipe) => equipe.titulo).join(', '),
                  },
                  {
                    rotulo: 'Escopo de unidades',
                    valor:
                      escopo?.irrestrito === true
                        ? 'Todas as unidades'
                        : escopo && escopo.unidades.length > 0
                          ? escopo.unidades.map((unidade) => unidade.titulo).join(', ')
                          : '—',
                  },
                  {
                    rotulo: 'Permissões efetivas',
                    valor: <span className="tabular-nums">{permissoes.length}</span>,
                  },
                ]}
              />
            </div>

            <Alerta tom="info">
              Nome, foto e celular são alterados pelo administrador enquanto{' '}
              <code className="font-mono text-xs">PATCH /api/v1/usuarios/&#123;uuid&#125;</code> e o
              upload de arquivos não estiverem publicados pela API.
            </Alerta>
          </CorpoCartao>
        </Cartao>

        <Cartao semPadding>
          <CabecalhoCartao
            titulo="Senha"
            acoes={
              !abrirSenha ? (
                <Botao
                  variante="contorno"
                  tamanho="sm"
                  onClick={() => setAbrirSenha(true)}
                  iconeEsquerda={<IconeCadeado className="h-4 w-4" />}
                >
                  Alterar senha
                </Botao>
              ) : null
            }
          />

          {abrirSenha ? (
            <form onSubmit={trocarSenha} noValidate>
              <CorpoCartao className="flex flex-col gap-4">
                {formulario.erroGeral ? (
                  <Alerta tom="perigo">{formulario.erroGeral}</Alerta>
                ) : null}

                <Campo rotulo="Senha atual" obrigatorio erro={formulario.erros.senhaAtual}>
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      type="password"
                      autoComplete="current-password"
                      value={formulario.valores.senhaAtual}
                      temErro={atributos.temErro}
                      onChange={(evento) => formulario.definir('senhaAtual', evento.target.value)}
                    />
                  )}
                </Campo>

                <Campo rotulo="Nova senha" obrigatorio erro={formulario.erros.senhaNova}>
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      type="password"
                      autoComplete="new-password"
                      value={formulario.valores.senhaNova}
                      temErro={atributos.temErro}
                      onChange={(evento) => formulario.definir('senhaNova', evento.target.value)}
                    />
                  )}
                </Campo>

                <ForcaSenha
                  senha={formulario.valores.senhaNova}
                  email={usuario.email}
                  nome={usuario.nome}
                />

                <Campo
                  rotulo="Confirmar nova senha"
                  obrigatorio
                  erro={formulario.erros.confirmacaoSenhaNova}
                >
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      type="password"
                      autoComplete="new-password"
                      value={formulario.valores.confirmacaoSenhaNova}
                      temErro={atributos.temErro}
                      onChange={(evento) =>
                        formulario.definir('confirmacaoSenhaNova', evento.target.value)
                      }
                    />
                  )}
                </Campo>

                <p className="text-xs text-conteudo-fraco">
                  Trocar a senha encerra todas as outras sessões da sua conta.
                </p>
              </CorpoCartao>
              <RodapeCartao>
                {!trocaObrigatoria && (
                  <Botao
                    variante="contorno"
                    onClick={() => {
                      formulario.reiniciar()
                      setAbrirSenha(false)
                    }}
                  >
                    Cancelar
                  </Botao>
                )}
                <Botao type="submit" carregando={formulario.enviando} disabled={!podeEnviar}>
                  Alterar senha
                </Botao>
              </RodapeCartao>
            </form>
          ) : (
            <CorpoCartao>
              <p className="text-sm text-conteudo-suave">
                Sua senha é conhecida apenas por você. Ninguém no sistema — inclusive o
                administrador — consegue vê-la; o que existe é o envio de um convite para você
                definir uma nova.
              </p>
            </CorpoCartao>
          )}
        </Cartao>

        <Cartao semPadding className="lg:col-span-2">
          <CabecalhoCartao
            titulo="Sessões ativas"
            descricao="Cada login é uma sessão. Encerrar todas obriga um novo login em todos os dispositivos."
            acoes={
              <Botao
                variante="contorno"
                tamanho="sm"
                onClick={() => setConfirmarSairDeTodos(true)}
              >
                Encerrar todas as sessões
              </Botao>
            }
          />
          <CorpoCartao>
            {sessoes.isPending ? (
              <p className="text-sm text-conteudo-fraco">Carregando sessões…</p>
            ) : sessoes.isError ? (
              <p className="text-sm text-conteudo-fraco">
                Não foi possível listar as sessões agora.
              </p>
            ) : (sessoes.data?.length ?? 0) === 0 ? (
              <p className="text-sm text-conteudo-fraco">Nenhuma sessão registrada.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda">
                {sessoes.data?.map((sessao) => (
                  <li
                    key={sessao.familiaUuid}
                    className="flex flex-wrap items-center justify-between gap-2 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-conteudo">
                        {sessao.dispositivo ?? 'Dispositivo não identificado'}
                        {sessao.atual ? (
                          <Etiqueta tom="sucesso" className="ml-2">
                            Esta sessão
                          </Etiqueta>
                        ) : null}
                      </p>
                      <p className="text-xs text-conteudo-fraco">
                        IP {sessao.ipMascarado ?? '—'} · última atividade{' '}
                        {formatarTempoRelativo(sessao.ultimaRotacaoEm)}
                      </p>
                    </div>
                    <span className="text-xs tabular-nums text-conteudo-fraco">
                      expira em {formatarDataHora(sessao.expiraEm)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CorpoCartao>
        </Cartao>
      </div>

      <Confirmacao
        aberto={confirmarSairDeTodos}
        onFechar={() => setConfirmarSairDeTodos(false)}
        onConfirmar={() => {
          void autenticacaoApi.sairDeTodos().then(() => {
            void sair().then(() => navegar('/entrar', { replace: true }))
          })
        }}
        titulo="Encerrar todas as sessões?"
        rotuloConfirmar="Encerrar tudo"
        varianteConfirmar="perigo"
        descricao="Você será desconectado em todos os dispositivos, inclusive aqui, e precisará entrar de novo."
      />
    </>
  )
}
